/**
 * ProfileHeader
 *
 * Displays user avatar, name, email, role, and share button.
 */

import { Share2 } from 'lucide-react';
import { getInitials, getAvatarColor } from '@/utils/formatters';

interface ProfileHeaderProps {
  userId: string;
  userName: string;
  userEmail: string;
  avatarUrl?: string;
  isOwnProfile: boolean;
  onShare?: () => void;
}

export function ProfileHeader({
  userName,
  userEmail,
  avatarUrl,
  isOwnProfile,
  onShare,
}: ProfileHeaderProps) {
  const initials = getInitials(userName);

  return (
    <div className="flex items-center gap-4 px-6 py-5 border-b border-border-primary bg-surface-secondary/30">
      {/* Avatar */}
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={userName}
          className="w-14 h-14 rounded-full ring-2 ring-border-primary"
        />
      ) : (
        <div
          className={`w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold text-white shadow-lg ${getAvatarColor(userName)}`}
        >
          {initials}
        </div>
      )}

      {/* Info */}
      <div className="flex-1 min-w-0">
        <h1 className="text-[15px] font-semibold text-text-primary truncate">
          {userName}
        </h1>
        <p className="text-[11px] text-text-tertiary truncate">{userEmail}</p>
        <p className="text-[10px] text-text-quaternary mt-0.5">
          {isOwnProfile ? 'Il tuo profilo' : 'Membro del team'}
        </p>
      </div>

      {/* Share button */}
      {isOwnProfile && onShare && (
        <button
          onClick={onShare}
          className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-[11px] font-medium text-accent-violet bg-accent-violet/10 hover:bg-accent-violet/20 transition-colors"
        >
          <Share2 className="w-3.5 h-3.5" />
          <span>Condividi profilo</span>
        </button>
      )}
    </div>
  );
}
